import { useMsal } from '@azure/msal-react';
import { capitalize } from 'lodash';
import {
  ArrowRightIcon,
  ArrowRightLeftIcon,
  BarChart3Icon,
  BrainCogIcon,
  BuildingIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  CommandIcon,
  Files,
  FilterIcon,
  FlameIcon,
  GaugeIcon,
  GitFork,
  HomeIcon,
  Landmark,
  LayoutDashboardIcon,
  LogOutIcon,
  type LucideIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  MoveHorizontalIcon,
  SearchIcon,
  SettingsIcon,
  UsersRoundIcon,
  WorkflowIcon
} from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import { CustomerFeedbackDialog } from '@/components/CustomerFeedbackDialog';
import { GlobalCommandPalette } from '@/components/GlobalCommandPalette';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useMainSidebar } from '@/hooks/useMainSidebar';
import { useOdooAuth } from '@/contexts/OdooAuthContext';
import { useAuth } from '@/lib/auth';
import { logoutFromOdoo } from '@/lib/odooAuth';

export interface AdditionalBreadcrumbItem {
  label: string;
  onClick?: () => void;
  to?: string;
}

interface NavShortcut {
  keys: string[];
  title: string;
  url: string;
}

interface RouteItem {
  badge?: string;
  children?: RouteItem[];
  department?: string;
  disabled?: boolean;
  hideInSidebar?: boolean;
  icon?: LucideIcon;
  iconClassName?: string;
  title: string;
  url: string;
}

interface RouteSection {
  items: RouteItem[];
  title: string;
}

const GO_SHORTCUT_OVERRIDES: Record<string, string> = {
  '/investors/operations': 'o',
  '/portfolio/reporting': 'r'
};

function buildGoShortcuts(routeSections: RouteSection[]): NavShortcut[] {
  const candidates = buildShortcutCandidates(routeSections);
  const usedSecondKeys = new Set<string>();
  const shortcutsByUrl = new Map<string, NavShortcut>();
  const getNormalizedTitle = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9]/g, '');

  for (const candidate of candidates) {
    const overriddenKey = GO_SHORTCUT_OVERRIDES[candidate.url];
    if (!overriddenKey || usedSecondKeys.has(overriddenKey)) {
      continue;
    }

    usedSecondKeys.add(overriddenKey);
    shortcutsByUrl.set(candidate.url, {
      ...candidate,
      keys: ['g', overriddenKey]
    });
  }

  const remainingCandidates = candidates.filter(
    candidate => !shortcutsByUrl.has(candidate.url)
  );

  const firstLetterCounts = new Map<string, number>();
  for (const candidate of remainingCandidates) {
    const normalized = getNormalizedTitle(candidate.title);
    const firstLetter = normalized[0];
    if (!firstLetter) {
      continue;
    }

    firstLetterCounts.set(
      firstLetter,
      (firstLetterCounts.get(firstLetter) ?? 0) + 1
    );
  }

  // If the first letter is unique, always reserve it.
  for (const candidate of remainingCandidates) {
    const normalized = getNormalizedTitle(candidate.title);
    const firstLetter = normalized[0];
    if (!firstLetter) {
      continue;
    }

    if (
      firstLetterCounts.get(firstLetter) === 1 &&
      !usedSecondKeys.has(firstLetter)
    ) {
      usedSecondKeys.add(firstLetter);
      shortcutsByUrl.set(candidate.url, {
        ...candidate,
        keys: ['g', firstLetter]
      });
    }
  }

  for (const candidate of remainingCandidates.sort((a, b) =>
    a.title.localeCompare(b.title)
  )) {
    if (shortcutsByUrl.has(candidate.url)) {
      continue;
    }

    const normalized = getNormalizedTitle(candidate.title);
    const preferredChars: string[] = [];

    for (const char of normalized) {
      if (!preferredChars.includes(char)) {
        preferredChars.push(char);
      }
    }

    let secondKey = preferredChars.find(char => !usedSecondKeys.has(char));

    if (!secondKey) {
      secondKey = [...'abcdefghijklmnopqrstuvwxyz0123456789'].find(
        char => !usedSecondKeys.has(char)
      );
    }

    const safeSecondKey = secondKey ?? 'x';
    usedSecondKeys.add(safeSecondKey);

    shortcutsByUrl.set(candidate.url, {
      ...candidate,
      keys: ['g', safeSecondKey]
    });
  }

  return [...shortcutsByUrl.values()];
}

function buildShortcutCandidates(routeSections: RouteSection[]): NavShortcut[] {
  const candidates: NavShortcut[] = [];

  for (const section of routeSections) {
    for (const item of section.items) {
      if (item.hideInSidebar || item.disabled) {
        continue;
      }

      if (item.children) {
        for (const child of item.children) {
          candidates.push({
            keys: [],
            title: child.title,
            url: child.url
          });
        }

        continue;
      }

      candidates.push({
        keys: [],
        title: item.title,
        url: item.url
      });
    }
  }

  return candidates;
}

function createKeytipTooltip(title: string, keys?: string[]) {
  return {
    children: (
      <div className="flex items-center gap-2">
        <span>{keys ? `Go to ${title}` : title}</span>
        {keys && (
          <kbd className="border-border/80 bg-background/60 text-foreground pointer-events-none inline-flex h-5 items-center rounded border px-1.5 font-mono text-[10px] font-medium select-none">
            {keytipLabel(keys)}
          </kbd>
        )}
      </div>
    ),
    className: ' bg-background/70 text-foreground shadow-sm backdrop-blur-sm',
    hideArrow: true
  };
}

function findCurrentRoute(pathname: string): {
  child?: RouteItem;
  item: RouteItem;
  remainingSegments: string[];
  section: RouteSection;
} | null {
  for (const section of routes) {
    for (const item of section.items) {
      // Check if item has children
      if (item.children) {
        for (const child of item.children) {
          if (pathname.startsWith(child.url)) {
            const remainingPath = pathname.slice(child.url.length);
            const remainingSegments = remainingPath
              .split('/')
              .filter(s => s.length > 0);
            return { child, item, remainingSegments, section };
          }
        }
      }
      // Check exact or prefix match for items without children
      if (pathname.startsWith(item.url)) {
        const remainingPath = pathname.slice(item.url.length);
        const remainingSegments = remainingPath
          .split('/')
          .filter(s => s.length > 0);
        return { item, remainingSegments, section };
      }
    }
  }
  return null;
}

function InlineKeytip({
  keys,
  sidebarOpen
}: {
  keys?: string[];
  sidebarOpen: boolean;
}) {
  if (!keys) {
    return null;
  }

  return (
    <span
      className={
        sidebarOpen
          ? 'bg-background text-muted-foreground pointer-events-none ml-auto inline-flex h-4 items-center rounded border px-1 font-mono text-[9px] leading-none whitespace-nowrap'
          : 'bg-background text-muted-foreground pointer-events-none absolute top-1/2 left-full z-20 ml-1 inline-flex h-4 -translate-y-1/2 items-center rounded border px-1 font-mono text-[9px] leading-none whitespace-nowrap shadow-sm'
      }
    >
      {keytipLabel(keys)}
    </span>
  );
}

function isTypingInInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      'input, textarea, select, [contenteditable="true"], [role="textbox"]'
    )
  );
}

function keytipLabel(keys: string[]): string {
  return keys.map(key => key.toUpperCase()).join(' then ');
}

const routes: RouteSection[] = [
  {
    items: [
      { hideInSidebar: true, icon: HomeIcon, title: 'Home', url: '/firm/home' },
      { icon: MessageCircleIcon, title: 'Chatbot', url: '/firm/chatbot' },
      {
        badge: 'Soon',
        disabled: true,
        icon: BuildingIcon,
        title: 'CRM',
        url: '/firm/crm'
      },
      { icon: WorkflowIcon, title: 'Workflows', url: '/firm/workflows/todo' },
      { icon: FilterIcon, title: 'Pipelines', url: '/investments/pipelines' },
      {
        hideInSidebar: true,
        icon: MessageSquareIcon,
        title: 'Customer Feedback',
        url: '/customer-requests'
      }
    ],
    title: 'Firm'
  },
  {
    items: [
      { icon: ArrowRightIcon, title: 'Direct', url: '/investments/direct' },
      {
        icon: ArrowRightLeftIcon,
        title: 'Secondary',
        url: '/investments/secondary'
      },
      {
        badge: 'Soon',
        disabled: true,
        icon: BrainCogIcon,
        title: 'Market Intelligence',
        url: '/investments/market-intelligence'
      }
    ],
    title: 'Investment'
  },
  {
    items: [
      { icon: Files, title: 'Monitoring', url: '/portfolio/monitoring' },
      { icon: BarChart3Icon, title: 'Reporting', url: '/portfolio/reporting' }
    ],
    title: 'Portfolio'
  },
  {
    items: [
      {
        icon: GitFork,
        iconClassName: 'rotate-180',
        title: 'Funds',
        url: '/fund-management/funds'
      },
      {
        badge: 'Soon',
        disabled: true,
        icon: GaugeIcon,
        title: 'Dashboards',
        url: '/fund-management/dashboards'
      },
      {
        department: 'Financial Operations & Treasury Management',
        icon: Landmark,
        title: 'Allocations',
        url: '/fund-management/allocations'
      },
      {
        icon: MoveHorizontalIcon,
        title: 'Relative Value',
        url: '/fund-management/relative-value'
      },
      {
        badge: 'Soon',
        disabled: true,
        icon: FlameIcon,
        title: 'Dry Powder',
        url: '/fund-management/dry-powder'
      }
    ],
    title: 'Fund Management'
  },
  {
    items: [
      {
        badge: 'Soon',
        disabled: true,
        icon: SettingsIcon,
        title: 'Operations',
        url: '/investors/operations'
      },
      {
        badge: 'Soon',
        disabled: true,
        icon: LayoutDashboardIcon,
        title: 'Dashboards',
        url: '/investors/dashboards'
      }
    ],
    title: 'Investors'
  },
  {
    items: [
      {
        department: 'Administration',
        icon: UsersRoundIcon,
        title: 'Committees',
        url: '/committees'
      }
    ],
    title: 'Admin'
  }
];

export function Layout() {
  const { isOpen, setIsOpen } = useMainSidebar();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { department } = useOdooAuth();
  const { instance } = useMsal();
  const [isCrmOpen, setIsCrmOpen] = useState(true);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [showAllKeytips, setShowAllKeytips] = useState(false);
  const showAllKeytipsRef = useRef(false);
  const keySequenceRef = useRef<string[]>([]);
  const keySequenceTimeoutRef = useRef<null | number>(null);
  const [additionalBreadcrumbs, setAdditionalBreadcrumbs] = useState<
    AdditionalBreadcrumbItem[]
  >([]);
  const goShortcuts = useMemo(() => buildGoShortcuts(routes), []);
  const shortcutByUrl = useMemo(
    () => new Map(goShortcuts.map(shortcut => [shortcut.url, shortcut])),
    [goShortcuts]
  );

  const addBreadcrumb = useCallback((item: AdditionalBreadcrumbItem) => {
    setAdditionalBreadcrumbs(prev => [...prev, item]);
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setAdditionalBreadcrumbs([]);
  }, []);

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs, pathname]);

  const outletNode = useMemo(
    () => (
      <Outlet
        context={{ addBreadcrumb, additionalBreadcrumbs, clearBreadcrumbs }}
      />
    ),
    [addBreadcrumb, additionalBreadcrumbs, clearBreadcrumbs]
  );

  const handleLogout = async () => {
    // Logout from Odoo first
    try {
      await logoutFromOdoo();
    } catch (error) {
      console.error('Error logging out from Odoo:', error);
    }
    // Then logout from Azure
    void instance.logoutRedirect({
      postLogoutRedirectUri:
        import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin
    });
  };

  const isActive = (url: string) => pathname.startsWith(url);

  // Handle Ctrl+K / Cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsCommandPaletteOpen(open => !open);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Show all sidebar keytips while Alt is held
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        (event.code === 'AltLeft' || event.code === 'AltRight') &&
        !showAllKeytipsRef.current
      ) {
        showAllKeytipsRef.current = true;
        setShowAllKeytips(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        (event.code === 'AltLeft' || event.code === 'AltRight') &&
        showAllKeytipsRef.current
      ) {
        showAllKeytipsRef.current = false;
        setShowAllKeytips(false);
      }
    };

    const handleBlur = () => {
      if (!showAllKeytipsRef.current) {
        return;
      }

      showAllKeytipsRef.current = false;
      setShowAllKeytips(false);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Handle G-based navigation shortcuts
  useEffect(() => {
    const clearSequence = () => {
      keySequenceRef.current = [];

      if (keySequenceTimeoutRef.current) {
        window.clearTimeout(keySequenceTimeoutRef.current);
        keySequenceTimeoutRef.current = null;
      }
    };

    const armSequenceTimeout = () => {
      if (keySequenceTimeoutRef.current) {
        window.clearTimeout(keySequenceTimeoutRef.current);
      }

      keySequenceTimeoutRef.current = window.setTimeout(() => {
        keySequenceRef.current = [];
        keySequenceTimeoutRef.current = null;
      }, 1200);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingInInput(event.target)) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.key.length !== 1) {
        return;
      }

      const key = event.key.toLowerCase();

      if (keySequenceRef.current.length === 0) {
        if (key !== 'g') {
          return;
        }

        event.preventDefault();
        keySequenceRef.current = ['g'];
        armSequenceTimeout();
        return;
      }

      event.preventDefault();

      const nextSequence = [...keySequenceRef.current, key];
      const matchingShortcuts = goShortcuts.filter(shortcut =>
        shortcut.keys
          .slice(0, nextSequence.length)
          .every((shortcutKey, index) => shortcutKey === nextSequence[index])
      );

      if (matchingShortcuts.length === 0) {
        if (key === 'g') {
          keySequenceRef.current = ['g'];
          armSequenceTimeout();
        } else {
          clearSequence();
        }
        return;
      }

      keySequenceRef.current = nextSequence;
      armSequenceTimeout();

      const exactMatch = matchingShortcuts.find(
        shortcut => shortcut.keys.length === nextSequence.length
      );
      const hasLongerMatches = matchingShortcuts.some(
        shortcut => shortcut.keys.length > nextSequence.length
      );

      if (exactMatch && !hasLongerMatches) {
        navigate(exactMatch.url);
        clearSequence();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      clearSequence();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goShortcuts, navigate]);

  return (
    <SidebarProvider
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <Sidebar collapsible="icon">
        <SidebarHeader className="relative px-4 py-2 group-data-[collapsible=icon]:h-[63px] group-data-[collapsible=icon]:px-0">
          <img
            alt="Star Mountain Capital"
            className="h-[47px] w-[108px] group-data-[collapsible=icon]:hidden"
            src="/smc-logo.svg"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="text-muted-foreground absolute top-0 right-2.25 bottom-0 my-auto hidden md:inline-flex" />
            </TooltipTrigger>
            <TooltipContent
              className="bg-background/70 text-foreground flex items-center gap-2 shadow-sm backdrop-blur-sm"
              hideArrow
              side="right"
            >
              <span>Toggle sidebar</span>
              <kbd className="border-border/80 bg-background/60 text-foreground pointer-events-none inline-flex h-5 items-center rounded border px-1.5 font-mono text-[10px] font-medium select-none">
                CTRL G
              </kbd>
            </TooltipContent>
          </Tooltip>
          {showAllKeytips && isOpen && (
            <span className="bg-background text-muted-foreground pointer-events-none absolute top-2 right-10 inline-flex h-4 items-center rounded border px-1 font-mono text-[9px] leading-none">
              CTRL then G
            </span>
          )}
        </SidebarHeader>
        <SidebarContent>
          <div className="flex flex-col gap-1.5">
            {routes.map((section, sectionIndex) => {
              const visibleItems = section.items.filter(
                item =>
                  !item.hideInSidebar &&
                  (!item.department || item.department === department)
              );

              if (visibleItems.length === 0) {
                return null;
              }

              return (
              <Fragment key={section.title}>
                <SidebarGroup>
                  <SidebarGroupLabel>{section.title}</SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {visibleItems.map(item => {
                          const Icon = item.icon;

                          if (item.children) {
                            const shortcut = shortcutByUrl.get(item.url);
                            return (
                              <Collapsible
                                asChild
                                className="group/collapsible"
                                key={item.title}
                                onOpenChange={setIsCrmOpen}
                                open={isCrmOpen}
                              >
                                <SidebarMenuItem>
                                  <CollapsibleTrigger asChild>
                                    <SidebarMenuButton
                                      className={
                                        showAllKeytips && isOpen
                                          ? 'relative'
                                          : undefined
                                      }
                                      forceTooltipOpen={false}
                                      isActive={isActive(item.url)}
                                      tooltip={createKeytipTooltip(
                                        item.title,
                                        shortcut?.keys
                                      )}
                                    >
                                      {Icon && (
                                        <Icon className={item.iconClassName} />
                                      )}
                                      <span>{item.title}</span>
                                      <ChevronRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                      {item.badge && (
                                        <Badge variant="secondary">
                                          {item.badge}
                                        </Badge>
                                      )}
                                      {showAllKeytips && isOpen && (
                                        <InlineKeytip
                                          keys={shortcut?.keys}
                                          sidebarOpen={isOpen}
                                        />
                                      )}
                                    </SidebarMenuButton>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <SidebarMenuSub>
                                      {item.children.map(child => (
                                        <SidebarMenuSubItem key={child.title}>
                                          <SidebarMenuSubButton
                                            asChild
                                            className={
                                              showAllKeytips && isOpen
                                                ? 'relative'
                                                : undefined
                                            }
                                            forceTooltipOpen={false}
                                            isActive={isActive(child.url)}
                                            tooltip={createKeytipTooltip(
                                              child.title,
                                              shortcutByUrl.get(child.url)?.keys
                                            )}
                                          >
                                            <Link to={child.url}>
                                              <span>{child.title}</span>
                                              {showAllKeytips && isOpen && (
                                                <InlineKeytip
                                                  keys={
                                                    shortcutByUrl.get(child.url)
                                                      ?.keys
                                                  }
                                                  sidebarOpen={isOpen}
                                                />
                                              )}
                                            </Link>
                                          </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                      ))}
                                    </SidebarMenuSub>
                                  </CollapsibleContent>
                                </SidebarMenuItem>
                              </Collapsible>
                            );
                          }

                          if (item.disabled) {
                            return (
                              <SidebarMenuItem key={item.title}>
                                <SidebarMenuButton
                                  disabled
                                  forceTooltipOpen={false}
                                  tooltip={createKeytipTooltip(item.title)}
                                >
                                  {Icon && (
                                    <Icon className={item.iconClassName} />
                                  )}
                                  <span>{item.title}</span>
                                  {item.badge && (
                                    <Badge
                                      className="ml-auto"
                                      variant="secondary"
                                    >
                                      {item.badge}
                                    </Badge>
                                  )}
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            );
                          }

                          const shortcut = shortcutByUrl.get(item.url);
                          return (
                            <SidebarMenuItem key={item.title}>
                              <SidebarMenuButton
                                asChild
                                className={
                                  showAllKeytips && isOpen
                                    ? 'relative'
                                    : undefined
                                }
                                forceTooltipOpen={false}
                                isActive={isActive(item.url)}
                                tooltip={createKeytipTooltip(
                                  item.title,
                                  shortcut?.keys
                                )}
                              >
                                <Link to={item.url}>
                                  {Icon && (
                                    <Icon className={item.iconClassName} />
                                  )}
                                  <span>{item.title}</span>
                                  {showAllKeytips && isOpen && (
                                    <InlineKeytip
                                      keys={shortcut?.keys}
                                      sidebarOpen={isOpen}
                                    />
                                  )}
                                </Link>
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                {sectionIndex < routes.length - 1 && (
                  <SidebarSeparator className="w-[calc(100%-theme(spacing.4)))]!" />
                )}
              </Fragment>
              );
            })}
          </div>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    size="lg"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 text-sm font-semibold text-white">
                      {user.name?.charAt(0) ?? 'U'}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left leading-none">
                      <span className="truncate text-sm font-semibold">
                        {user.name}
                      </span>
                      <span className="text-muted-foreground truncate text-xs">
                        {user.username}
                      </span>
                    </div>
                    <ChevronsUpDownIcon className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
                  side="right"
                  sideOffset={4}
                >
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm leading-none font-medium">
                        {user.name}
                      </p>
                      <p className="text-muted-foreground text-xs leading-none">
                        {user.username}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setIsFeedbackDialogOpen(true);
                    }}
                  >
                    <MessageSquareIcon />
                    <span>Send Feedback</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      void handleLogout();
                    }}
                    variant="destructive"
                  >
                    <LogOutIcon />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset className="h-dvh min-w-0">
        {pathname.startsWith('/firm/chatbot') ||
        pathname.startsWith('/firm/workflows') ? (
          <div className="bg-sidebar sticky top-0 z-10 flex h-16 shrink-0 items-center justify-end border-b px-4">
            <SidebarTrigger className="text-muted-foreground mr-auto md:hidden" />
            {/* Global Search Trigger */}
            <button
              className="border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground hidden h-9 w-96 items-center gap-2 rounded-md border px-3 text-sm transition-colors md:flex"
              onClick={() => {
                setIsCommandPaletteOpen(true);
              }}
            >
              <SearchIcon className="size-4" />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100 select-none">
                <CommandIcon className="size-3" />K
              </kbd>
            </button>
          </div>
        ) : (
          <div className="bg-sidebar sticky top-0 z-10 flex h-16 shrink-0 items-center gap-4 border-b pr-4 pl-3 md:pl-14">
            <SidebarTrigger className="text-muted-foreground md:hidden" />
            <Breadcrumb className="shrink-0">
              <BreadcrumbList>
                {(() => {
                  const route = findCurrentRoute(pathname);
                  if (!route) return null;

                  const { child, item, remainingSegments, section } = route;

                  const baseUrl = child ? child.url : item.url;

                  // Match Figma: "Investments > Direct Investments > Deal Score"
                  const breadcrumbItems: { label: string; url: string }[] =
                    section.title === 'Investment'
                      ? [
                          { label: 'Investments', url: baseUrl },
                          { label: 'Direct Investments', url: baseUrl }
                        ]
                      : [
                          {
                            label: child
                              ? `${section.title} - ${item.title} - ${child.title}`
                              : `${section.title} - ${item.title}`,
                            url: baseUrl
                          }
                        ];

                  const segmentLabel = (seg: string) =>
                    seg === 'ic-voting'
                      ? 'IC Voting'
                      : seg
                          .split('-')
                          .map(word => capitalize(word))
                          .join(' ');

                  let currentUrl = baseUrl;
                  let i = 0;
                  while (i < remainingSegments.length) {
                    const segment = remainingSegments[i];
                    currentUrl = `${currentUrl}/${segment}`;

                    // If this is the first remaining segment (ID), skip it in the label
                    // but include it in the URL
                    if (i === 0 && remainingSegments.length > 1) {
                      const nextSegment = remainingSegments[i + 1];
                      const nextUrl = `${currentUrl}/${nextSegment}`;
                      const nextLabel = segmentLabel(nextSegment);
                      // Avoid duplicate "Investments" when we already have it for this section
                      if (
                        section.title !== 'Investment' ||
                        nextLabel !== 'Investments'
                      ) {
                        breadcrumbItems.push({
                          label: nextLabel,
                          url: nextUrl
                        });
                      }
                      currentUrl = nextUrl;
                      i += 2; // Skip both segments
                    } else if (i === 0 && remainingSegments.length === 1) {
                      // Only ID segment, skip it entirely
                      i += 1;
                    } else if (
                      i > 0 &&
                      (remainingSegments[i - 1] === 'deal-score' ||
                        remainingSegments[i - 1] === 'allocations' ||
                        remainingSegments[i - 1] === 'deal-calculator' ||
                        remainingSegments[i - 1] === 'appendices')
                    ) {
                      // Score/allocation/calculator ID segment: skip (breadcrumb stops at section name)
                      i += 1;
                    } else {
                      const label = segmentLabel(segment);
                      if (
                        section.title !== 'Investment' ||
                        label !== 'Investments'
                      ) {
                        breadcrumbItems.push({
                          label,
                          url: currentUrl
                        });
                      }
                      i += 1;
                    }
                  }

                  return (
                    <>
                      {breadcrumbItems.map((crumb, index) => {
                        const isLast =
                          index === breadcrumbItems.length - 1 &&
                          additionalBreadcrumbs.length === 0;

                        return (
                          <Fragment key={`${crumb.label}-${crumb.url}`}>
                            {index > 0 && <BreadcrumbSeparator />}
                            <BreadcrumbItem>
                              {isLast ? (
                                <BreadcrumbPage
                                  className="cursor-pointer"
                                  onClick={clearBreadcrumbs}
                                >
                                  {crumb.label}
                                </BreadcrumbPage>
                              ) : (
                                <BreadcrumbLink asChild>
                                  <Link to={crumb.url}>{crumb.label}</Link>
                                </BreadcrumbLink>
                              )}
                            </BreadcrumbItem>
                          </Fragment>
                        );
                      })}
                    </>
                  );
                })()}
                {additionalBreadcrumbs.map(item => {
                  const key = `${item.label}-${item.to ?? 'page'}`;
                  return (
                    <Fragment key={key}>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        {item.to ? (
                          <BreadcrumbLink asChild>
                            <Link to={item.to}>{item.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{item.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </Fragment>
                  );
                })}
              </BreadcrumbList>
            </Breadcrumb>

            {/* Global Search Trigger */}
            <div className="ml-auto hidden min-w-0 flex-1 justify-end md:flex">
              <button
                className="border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground flex h-9 w-full max-w-96 min-w-0 items-center gap-2 rounded-md border px-3 text-sm transition-colors"
                onClick={() => {
                  setIsCommandPaletteOpen(true);
                }}
              >
                <SearchIcon className="size-4 shrink-0" />
                <span className="flex-1 truncate text-left">Search...</span>
                <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 shrink-0 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100 select-none">
                  <CommandIcon className="size-3" />K
                </kbd>
              </button>
            </div>
          </div>
        )}
        {outletNode}
      </SidebarInset>
      <CustomerFeedbackDialog
        onOpenChange={setIsFeedbackDialogOpen}
        open={isFeedbackDialogOpen}
        userEmail={user.username}
        userName={user.name}
      />
      <GlobalCommandPalette
        onOpenChange={setIsCommandPaletteOpen}
        open={isCommandPaletteOpen}
      />
    </SidebarProvider>
  );
}
